import {
  format as dateFnsFormat,
  getUnixTime,
  isValid,
  parseISO,
  fromUnixTime,
  getTime,
  startOfDay,
  subDays,
} from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { orderBy } from 'lodash';
import { Fragment, ReactNode, cloneElement, isValidElement } from 'react';

export type JsonObject = Record<string, unknown>;

export type FlatObject = Record<string, string>;

export type LoadState = 'unloaded' | 'loading' | 'loaded' | 'failed';

export type ViewMode = 'view' | 'edit' | 'add';

export const isWriteMode = (mode: ViewMode): boolean => ['edit', 'add'].includes(mode);

export const isAddMode = (mode: ViewMode): boolean => mode === 'add';

export type UiOption = {
  label: ReactNode;
  labelSimple?: string;
  value: string;
  description?: string;
  match?: boolean;
  disabled?: boolean;
  tooltip?: string;
  meta?: Record<string, unknown>;
  key?: string;
};

export const getUiOptionLabel = (option: UiOption): string => {
  if (typeof option.label === 'string') {
    return option.label;
  }
  if (option.labelSimple) {
    return option.labelSimple;
  }
  return String(option.label);
};

export const sortByLabel = (options: UiOption[]): UiOption[] =>
  orderBy<UiOption>(options, [(option) => getUiOptionLabel(option).toLowerCase()], ['asc']);

export enum DateFormat {
  MEDIUM = 'yyyy-MM-dd HH:mm',
  FULL = 'yyyy-MM-dd HH:mm:ss',
  FINE = 'yyyy-MM-dd HH:mm:ss.SSS',
  FRIEND = 'MMMM do, yyyy',
  NOTIME = 'yyyy-MM-dd',
  TIME_12H = 'h:mm a',
  TIMELINE = 'MMM d · HH:mm:ss',
}

export type NiceDate = {
  date: string;
  time: string;
  timeFull: string;
  formatted: string;
  dateObj: Date | null;
  timezone: string;
  offset: string;
};

export type DateVal = string | Date | number | null;

export type DateRangeValue = [Date | null, Date | null];

export type DateBound = string | Date | (() => string | Date);

export const resolveDateBound = (bound?: DateBound): string | Date =>
  typeof bound === 'function' ? bound() : (bound ?? '');

export const AWS_REGION_OPTS: UiOption[] = [
  { label: 'US', value: 'us', meta: { header: true } },
  { label: 'US East (Ohio)', value: 'us-east-2' },
  { label: 'US East (N. Virginia)', value: 'us-east-1' },
  { label: 'US West (N. California)', value: 'us-west-1' },
  { label: 'US West (Oregon)', value: 'us-west-2' },
  { label: 'US GovCloud East (Ohio)', value: 'us-gov-east-1' },
  { label: 'US GovCloud West (Oregon)', value: 'us-gov-west-1' },
  { label: 'Africa', value: 'africa', meta: { header: true } },
  { label: 'Africa (Cape Town)', value: 'af-south-1' },
  { label: 'Asia', value: 'asia', meta: { header: true } },
  { label: 'Asia Pacific (Hong Kong)', value: 'ap-east-1' },
  { label: 'Asia Pacific (Hyderabad)', value: 'ap-south-2' },
  { label: 'Asia Pacific (Jakarta)', value: 'ap-southeast-3' },
  { label: 'Asia Pacific (Malaysia)', value: 'ap-southeast-5' },
  { label: 'Asia Pacific (Melbourne)', value: 'ap-southeast-4' },
  { label: 'Asia Pacific (Mumbai)', value: 'ap-south-1' },
  { label: 'Asia Pacific (Osaka)', value: 'ap-northeast-3' },
  { label: 'Asia Pacific (Seoul)', value: 'ap-northeast-2' },
  { label: 'Asia Pacific (Singapore)', value: 'ap-southeast-1' },
  { label: 'Asia Pacific (Sydney)', value: 'ap-southeast-2' },
  { label: 'Asia Pacific (Tokyo)', value: 'ap-northeast-1' },
  { label: 'Canada', value: 'canada', meta: { header: true } },
  { label: 'Canada (Central)', value: 'ca-central-1' },
  { label: 'Canada West (Calgary)', value: 'ca-west-1' },
  { label: 'Europe', value: 'europe', meta: { header: true } },
  { label: 'Europe (Frankfurt)', value: 'eu-central-1' },
  { label: 'Europe (Ireland)', value: 'eu-west-1' },
  { label: 'Europe (London)', value: 'eu-west-2' },
  { label: 'Europe (Milan)', value: 'eu-south-1' },
  { label: 'Europe (Paris)', value: 'eu-west-3' },
  { label: 'Europe (Spain)', value: 'eu-south-2' },
  { label: 'Europe (Stockholm)', value: 'eu-north-1' },
  { label: 'Europe (Zurich)', value: 'eu-central-2' },
  { label: 'Middle East', value: 'middle east', meta: { header: true } },
  { label: 'Israel (Tel Aviv)', value: 'il-central-1' },
  { label: 'Middle East (Bahrain)', value: 'me-south-1' },
  { label: 'Middle East (UAE)', value: 'me-central-1' },
  { label: 'South America', value: '', meta: { header: true } },
  { label: 'South America (São Paulo)', value: 'sa-east-1' },
];

export const VERTEX_AI_REGION_OPTS: UiOption[] = [
  { label: 'Global', value: 'global-header', meta: { header: true } },
  { label: 'Global (Multi-region)', value: 'global' },

  { label: 'Multi-region', value: 'multi-region-header', meta: { header: true } },
  { label: 'US (Multi-region)', value: 'us' },
  { label: 'EU (Multi-region)', value: 'eu' },

  { label: 'Americas', value: 'americas-header', meta: { header: true } },
  { label: 'US Central (Iowa)', value: 'us-central1' },
  { label: 'US East (S. Carolina)', value: 'us-east1' },
  { label: 'US East (N. Virginia)', value: 'us-east4' },
  { label: 'US East (Ohio)', value: 'us-east5' },
  { label: 'US South (Dallas)', value: 'us-south1' },
  { label: 'US West (Oregon)', value: 'us-west1' },
  { label: 'US West (Los Angeles)', value: 'us-west2' },
  { label: 'US West (Salt Lake City)', value: 'us-west3' },
  { label: 'US West (Las Vegas)', value: 'us-west4' },
  { label: 'Canada (Montreal)', value: 'northamerica-northeast1' },
  { label: 'Canada (Toronto)', value: 'northamerica-northeast2' },
  { label: 'Brazil (São Paulo)', value: 'southamerica-east1' },
  { label: 'Chile (Santiago)', value: 'southamerica-west1' },

  { label: 'Europe', value: 'europe-header', meta: { header: true } },
  { label: 'Belgium', value: 'europe-west1' },
  { label: 'London, UK', value: 'europe-west2' },
  { label: 'Frankfurt, Germany', value: 'europe-west3' },
  { label: 'Netherlands', value: 'europe-west4' },
  { label: 'Zurich, Switzerland', value: 'europe-west6' },
  { label: 'Milan, Italy', value: 'europe-west8' },
  { label: 'Paris, France', value: 'europe-west9' },
  { label: 'Warsaw, Poland', value: 'europe-central2' },
  { label: 'Finland', value: 'europe-north1' },
  { label: 'Madrid, Spain', value: 'europe-southwest1' },

  { label: 'Asia Pacific', value: 'asia-pacific-header', meta: { header: true } },
  { label: 'Tokyo, Japan', value: 'asia-northeast1' },
  { label: 'Osaka, Japan', value: 'asia-northeast2' },
  { label: 'Seoul, South Korea', value: 'asia-northeast3' },
  { label: 'Singapore', value: 'asia-southeast1' },
  { label: 'Jakarta, Indonesia', value: 'asia-southeast2' },
  { label: 'Hong Kong', value: 'asia-east2' },
  { label: 'Taiwan', value: 'asia-east1' },
  { label: 'Mumbai, India', value: 'asia-south1' },
  { label: 'Delhi, India', value: 'asia-south2' },
  { label: 'Sydney, Australia', value: 'australia-southeast1' },
  { label: 'Melbourne, Australia', value: 'australia-southeast2' },

  { label: 'Middle East & Africa', value: 'mea-header', meta: { header: true } },
  { label: 'Tel Aviv, Israel', value: 'me-west1' },
  { label: 'Doha, Qatar', value: 'me-central1' },
  { label: 'Dammam, Saudi Arabia', value: 'me-central2' },
  { label: 'Johannesburg, South Africa', value: 'africa-south1' },
];

export const DATADOG_SITE_OPTS: UiOption[] = [
  { label: 'US1 (US East 1)', value: 'us1' },
  { label: 'US3 (US Central 1)', value: 'us3' },
  { label: 'US5 (US West 2)', value: 'us5' },
  { label: 'EU1 (Frankfurt)', value: 'eu1' },
  { label: 'AP1 (Tokyo)', value: 'ap1' },
  { label: 'US1-FED (GovCloud)', value: 'us1-fed' },
  { label: 'US1-GOV (GovCloud)', value: 'us1-gov' },
];

export const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const getRandomInt = (min: number, max: number): number =>
  Math.floor(Math.random() * (max - min + 1)) + min;

export const getIdFromLabel = (label: string): string =>
  String(label)
    .replace(/ +/g, '-')
    .replace(/[^A-Za-z0-9-]+/, '')
    .toLowerCase();

export const getDateObj = (val: DateVal): Date | null => {
  if (!val) {
    return null;
  }

  if (val instanceof Date) {
    return val;
  }

  let dateObj: Date | null = null;

  if (String(val).match(/^[0-9]+$/) || typeof val === 'number') {
    const valSeconds = String(val).length === 13 ? Math.floor(Number(val) / 1000) : Number(val); // convert ms timestamps to s timestamps
    dateObj = fromUnixTime(valSeconds);
  } else {
    dateObj = parseISO(val);
  }

  if (isValid(dateObj)) {
    return dateObj;
  }

  return null;
};

export const getDaysAgoDate = (daysAgo: number): Date => {
  return startOfDay(subDays(new Date(), daysAgo));
};

export const getTimestamp = (val: DateVal, useMs = false): number | null => {
  const dateObj = getDateObj(val);
  if (!dateObj) {
    return null;
  }

  return useMs ? getTime(dateObj) : getUnixTime(dateObj);
};

export const getNiceNumber = (val: number, options?: { decimalPlaces?: number }): string => {
  const { decimalPlaces } = options || {};

  const safeD = typeof decimalPlaces === 'undefined' ? 0 : Number(decimalPlaces);

  // toLocaleString adds the thousands separator in the default locale, and the min/max options limit the decimal places
  const localeOpts = {
    minimumFractionDigits: safeD,
    maximumFractionDigits: safeD,
  };

  const localVal = val.toLocaleString(undefined, localeOpts);

  // prevent display of negative sign when value is zero (i.e. when -0.34 is truncated to -0)
  if (localVal.match(/^-0.?0*$/)) {
    return '0';
  }

  return localVal;
};

export const getNiceDate = (val: DateVal, format?: DateFormat, timezone?: string): NiceDate => {
  const niceDate: NiceDate = {
    date: '',
    time: '',
    timeFull: '',
    formatted: '',
    dateObj: null,
    timezone: '',
    offset: '',
  };
  let dateObj = getDateObj(val);

  if (!dateObj) {
    return niceDate;
  }

  if (timezone) {
    dateObj = getTimezoneDateObj(dateObj);

    if (!dateObj) {
      return niceDate;
    }
  }

  niceDate.dateObj = dateObj;
  niceDate.date = dateFnsFormat(dateObj, 'yyyy-MM-dd');
  niceDate.time = dateFnsFormat(dateObj, 'HH:mm');
  niceDate.timeFull = dateFnsFormat(dateObj, 'HH:mm:ss');
  niceDate.formatted = dateFnsFormat(dateObj, format || DateFormat.MEDIUM);
  niceDate.timezone = timezone || '';
  niceDate.offset = timezone ? dateFnsFormat(dateObj, 'XXX') : '';

  return niceDate;
};

export const getTimezoneDateObj = (val: DateVal, timezone?: string): Date | null => {
  const dateObj = getDateObj(val);

  if (!dateObj) {
    return null;
  }

  const targetTimezone = timezone || getUserTimezone().timezone;

  return utcToZonedTime(dateObj, targetTimezone) || null;
};

export const getUserTimezone = (): { timezone: string; offset: string } => {
  try {
    return {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      offset: dateFnsFormat(new Date(), 'XXX'),
    };
  } catch {
    return {
      timezone: '',
      offset: '',
    };
  }
};

export const getFirstLastName = (
  name: string
): { first: string; last: string; hasName: boolean } => {
  const nameParts = name.split(' ');
  const first = nameParts.shift() || '';
  const last = nameParts.join(' ') || '';
  return { first, last, hasName: !!first || !!last };
};

export const joinReactNodes = (nodes: ReactNode[], glue: ReactNode): ReactNode[] => {
  const joinedNodes: ReactNode[] = [];
  const GlueEl = isValidElement(glue) ? glue : <Fragment>{glue}</Fragment>;

  nodes.forEach((node, index) => {
    const NodeEl = isValidElement(node) ? node : <Fragment>{node}</Fragment>;
    joinedNodes.push(cloneElement(NodeEl, { key: `node-${index}` }));
    if (index < nodes.length - 1) {
      joinedNodes.push(cloneElement(GlueEl, { key: `glue-${index}` }));
    }
  });
  return joinedNodes;
};

export const downloadCsv = (data: string | string[][], filename: string) => {
  let csvData = '';

  if (typeof data === 'string') {
    csvData = data;
  }

  if (Array.isArray(data)) {
    const csvRows: string[] = [];

    data.forEach((row) => {
      const csvRow = row.map((val) => `"${String(val).replace(/"+/g, '\\"')}"`);
      csvRows.push(csvRow.join(','));
    });

    csvData = csvRows.join('\r\n');
  }

  const blob = new Blob([csvData], { type: 'text/csv' });

  return downloadFile(blob, filename);
};

// https://stackoverflow.com/questions/50694881/how-to-download-file-in-react-js
export const downloadFile = (blobData: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blobData);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  setTimeout(() => window.URL.revokeObjectURL(url), 60 * 1000);
};

// https://stackoverflow.com/questions/27194359/javascript-pluralize-an-english-string
export const pluralize = (count: number, noun: string, suffix = 's') =>
  `${noun}${count !== 1 ? suffix : ''}`;

export const removePrefix = (str: string, prefix: string): string =>
  String(str).replace(new RegExp(`^${prefix}`), '');

export const printConsoleBanner = () => {
  const message = `
  ______  _____ 
 |  ____|| ____|
 | |__   | |__  
 |  __|  |___ \\ 
 | |      ___) |
 |_|     |____/                 

Come build with us! => human@surepath.ai                                                     
  `;

  console.log(message);
};

// https://emailregex.com/ w3c compliant version
export const isValidEmail = (value: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
  return emailRegex.test(value);
};

export const getNiceFilesize = (bytes: number): string => {
  if (bytes < 1024) {
    return bytes + 'b';
  }
  if (bytes < 1024 * 1024) {
    return (bytes / 1024).toFixed(2) + 'kb';
  }

  return (bytes / (1024 * 1024)).toFixed(2) + 'mb';
};

export const countLinesInFile = async (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    // Use FileReader to read the file content as text
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;

        if (!content.length) {
          resolve(0);
          return;
        }

        const lineCount = content.split('\n').length;
        resolve(lineCount);
      } catch (err) {
        console.error(err);
        reject(err as Error);
      }
    };

    reader.onerror = (err) => {
      console.error(err);
      reject(new Error());
    };

    // Start reading the file
    reader.readAsText(file);
  });
};

export const validateHexadecimalId = (
  inputValue: string,
  length?: number
): { isValid: boolean; error: string } => {
  if (!inputValue) {
    return { isValid: false, error: '' };
  }

  if (length && inputValue.length !== length) {
    return { isValid: false, error: '' };
  }

  const hexPattern = /^[0-9a-fA-F]+$/i;
  if (!hexPattern.test(inputValue)) {
    return {
      isValid: false,
      error: `ID must be a ${length && `${length}-character `}hexadecimal string (0-9, a-f, A-F)`,
    };
  }

  return { isValid: true, error: '' };
};

export const removeSlashes = (value: string): string => {
  if (!value || typeof value !== 'string') {
    return '';
  }

  return value.trim().replace(/\/+$/g, '').replace(/^\/+/g, '');
};

export const getInitials = (name: string): string => {
  const { first, last } = getFirstLastName(name);
  return `${first[0] || ''}${last[0] || ''}`.toUpperCase();
};

/**
 * Basic CIDR format validation. Supports IPv4 (e.g. 192.168.0.0/24) and IPv6.
 * Also accepts plain IP addresses (treated as /32 for IPv4 or /128 for IPv6).
 */
export const isValidCidr = (value: string): boolean => {
  const safeValue = value ? String(value).trim() : '';

  if (!safeValue) {
    return false;
  }

  // IPv4: xxx.xxx.xxx.xxx or xxx.xxx.xxx.xxx/nn (0-32)
  const ipv4Cidr =
    /^(?:(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9][0-9]|[0-9])\.){3}(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9][0-9]|[0-9])(?:\/(?:3[0-2]|[12][0-9]|[0-9]))?$/;
  if (ipv4Cidr.test(safeValue)) {
    return true;
  }

  // IPv6: basic format with colons and optional /prefix (0-128)
  const hasColons = safeValue.includes(':');
  const ipv6WithPrefix = /^[0-9a-fA-F:]+(?:\/(?:1[0-2][0-8]|[1-9][0-9]|[0-9]))?$/;
  if (hasColons && ipv6WithPrefix.test(safeValue)) {
    return true;
  }

  return false;
};
